<script setup>
import { ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';
import { useLayoutComponents } from '@/composables/layout.js';
import { useIcons } from '@/composables/icons.js';
import { useToast } from '@/composables/use-toast';
import { useUserStore } from '@/store/UserStore';

const userStore = useUserStore();

const { addToast } = useToast();
const { TextInput, PrimaryButton, Checkbox, ButtonLoading } = useLayoutComponents();
const { Envelope, ProfileCard } = useIcons();

const router = useRouter();
const errorMessage = ref('');
const loading = ref(false);

const form = ref({
    username: '',
    email: '',
    password: '',
    access_code: '',
    profile_image: null,
    termsAccepted: false
});

const alertError = ref({
    msgs: [],
    show: false
});

const usernameError = ref('');
const emailError = ref('');
const passwordError = ref('');
const accessCodeError = ref('');
const termsError = ref('');
const inputError = ref(false);

const register = async () => {
    alertError.value.msgs = [];
    alertError.value.show = false;

    if (!validateForm()) {
        alertError.value.show = true;
        errorMessage.value = alertError.value.msgs[0] || 'Validation failed';
        return;
    }

    try {
        loading.value = true;

        const userData = {
            email: form.value.email,
            access_code: form.value.access_code,
        };

        const response = await axios.post('/api/users/validate-access-code', userData);

        if (response.data.statusCode == 200) {
            //pinia user store
            userStore.setAccessCodeResponse(
                response.data.email, 
                response.data.accountType, 
                response.data.accessCode,
                response.data.flags
            );
            if (response.data.accountType === 'seller' || response.data.accountType === 'buyer') {
                router.push({
                    path: '/account-creation',
                    state: userData
                });
            } else if (response.data.accountType === 'rendery_dao') {
                router.push('/dao-account-creation');
            }
        }

    } catch (error) {
        console.error(error);
        addToast(error.response?.data?.message, 'error');
        errorMessage.value = error.response?.data?.message || 'An error occurred';
        alertError.value.msgs.push(errorMessage.value);
        alertError.value.show = true;
    } finally {
        loading.value = false;
    }
};

const validateForm = () => {
    usernameError.value = '';
    emailError.value = '';
    passwordError.value = '';
    termsError.value = '';
    accessCodeError.value = '';
    alertError.value.msgs = [];

    const missingRequiredFields = !form.value.email;
    if (missingRequiredFields) {
        addToast('Please fill in all required fields', 'error');
    }

    validateEmailInput();


    if (!form.value.termsAccepted) {
        termsError.value = 'You must accept the Terms and Conditions';
        if (!usernameError.value &&
            !emailError.value &&
            !passwordError.value &&
            !accessCodeError.value) {
            addToast(termsError.value, 'error');
        }
        return false;
    }

    return !emailError.value &&
        !accessCodeError.value &&
        !termsError.value &&
        form.value.termsAccepted;
};

const validateEmailInput = () => {
    const { email } = form.value;
    emailError.value = '';

    if (!email) {
        emailError.value = 'Email is required';
        return;
    }
    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)) {
        emailError.value = 'Please enter a valid email address';
        addToast(emailError.value, 'error');
    }
};

const haveAccessCode = ref(false);

watch(haveAccessCode, (newValue) => {
    if (!newValue) {
        form.value.access_code = '';
        accessCodeError.value = '';
    }
});
</script>
<template>
    <div class="register">
        <div class="register-card">
            <div class="logo-container">
                <img src="/favicon.ico" alt="logo" class="register-card__header-logo" />
            </div>
            <div class="register-card__header">
                <div class="register-card__header-text">
                    <h2 class="register-card__header-title" data-testid="register-form-title">Welcome!</h2>
                    <p class="register-card__header-subtitle">Let's get started creating your account.</p>
                    <span class="register-card__header-subtitle-link"><router-link to="/login"
                            class="text-echoes-primary hover:text-echoes-primary/80 font-semibold mr-1.5">Sign in</router-link>if
                        you already have an account </span>
                </div>
            </div>
            <div class="register-layout">
                <div class="register-right-section">
                    <form @submit.prevent="register" class="register-card__body-form" enctype="multipart/form-data"
                        novalidate>
                        <div class="register-card__body-form-group">
                            <TextInput v-model="form.email" type="text" id="email" :error="inputError"
                                placeholder="email address" dataTestId="email-input">
                                <template #icon>
                                    <Envelope class="w-5 h-5" :class="{ 'text-red-500': emailError }" />
                                </template>
                            </TextInput>
                        </div>
                        <div class="register-card__body-form-group" v-if="haveAccessCode">
                            <TextInput v-model="form.access_code" type="text" id="access_code" placeholder="access code"
                                :class="{ 'error-input': accessCodeError }"
                                dataTestId="access-code-input">
                                <template #icon>
                                    <ProfileCard class="w-5 h-5" :class="{ 'text-red-500': accessCodeError }" />
                                </template>
                            </TextInput>
                        </div>
                        <div class="flex flex-col gap-2 my-2 px-0.5">

                            <div class="space-x-1">
                            <Checkbox v-model="haveAccessCode" dataTestId="access-code-checkbox"/>
                            <label for="access_code" class="text-sm">
                                I have an access code (optional)
                            </label>
                        </div>
                        <div class="register-card__body-form-group">
                            <div class="space-x-1">
                                <Checkbox v-model="form.termsAccepted" required  dataTestId="terms-checkbox"/>
                                <label for="terms" class="text-sm">
                                    I agree to the <a href="https://portexai.com/tou" class="terms-link">Terms &
                                        Conditions</a>
                                </label>
                            </div>
                        </div>



                        </div>
                      
                        <div class="register-card__footer h-10">
                            <PrimaryButton type="primary" class="register-card__body-form-button" dataTestId="register-button">
                                <ButtonLoading v-if="loading" size="sm" text="Registering..." />
                                <template v-else>Register</template>
                            </PrimaryButton>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="postcss">
.register {
    @apply flex justify-center items-center h-[100dvh] w-screen px-4;
    background-image: linear-gradient(to bottom, #232323, #000000);
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
}

.register-card {
    @apply w-full max-w-sm p-4 pt-10 rounded-2xl shadow-lg mx-auto mt-24 relative bg-echoes-background-black border border-echoes-border-gray;
    backdrop-filter: blur(8px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3), 0 4px 6px rgba(0, 0, 0, 0.2);
}

.logo-container {
    @apply absolute left-1/2 transform -translate-x-1/2 -translate-y-1/2;
    top: 0;
}

.register-card__header-logo {
    @apply w-16 h-16;
    background: linear-gradient(135deg, #3f51b5, #ff4081);
    border-radius: 50%;
    position: relative;
    overflow: hidden;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

.register-card__header-logo::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at center, rgba(255, 255, 255, 0.2) 10%, transparent 70%);
}

.register-layout {
    @apply flex flex-col;
}


.register-right-section {
    @apply flex-1 flex flex-col;
}

.register-card__header {
    @apply flex flex-col justify-center items-center mb-3;
}

.register-card__header-text {
    @apply flex flex-col gap-1 2xl:gap-2;
}

.register-card__header-title {
    @apply text-2xl font-bold text-white text-center;
}

.register-card__header-subtitle {
    @apply text-base text-white/80 block font-semibold text-center;
}

.register-card__header-subtitle-link {
    @apply text-sm text-white/80 block text-center;
}

.register-card__body-form {
    @apply flex flex-col gap-2;
}

.register-card__body-form-group {
    @apply flex flex-col gap-2;
}

.register-card__body-form-button {
    @apply w-full shadow-lg h-full;
}

.register-card__footer {
    @apply text-center flex flex-col gap-2;
}



.terms-link {
    @apply text-echoes-primary hover:text-echoes-primary/80 font-medium;
}

.hidden-input {
    @apply hidden;
}

.error-input {
    @apply border-red-500 bg-red-100 bg-transparent;
}

@media (max-width: 768px) {
    .register-card {
        @apply max-w-sm;
    }
}
</style>